/* eslint-disable react/no-unknown-property -- Firebolt consumes css props. */
import { css } from '@firebolt-dev/css'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MapPinIcon, MessageSquareTextIcon, RefreshCwIcon, UsersIcon } from 'lucide-react'

import { buildMapIndex, searchMap } from '../map-search'
import { buildPresenceList, presenceActions } from '../presence'
import { refreshSocialMap, screenData, socialMapView, subscribeScreenData, teleportToDestination, worldStatus } from '../screen-data'
import { COLORS, MOTION, RADII, SHADOWS, TYPE, space } from '../theme'
import { ScreenShell } from './VoiceKit'

const resultMessages = {
  TELEPORTED: 'Сервер подтвердил перемещение.',
  RATE_LIMITED: 'Слишком много перемещений. Подожди и повтори.',
  STALE_DESTINATION: 'Место изменилось. Список обновлён.',
  DESTINATION_UNAVAILABLE: 'Это место больше недоступно.',
  DESTINATION_OUT_OF_BOUNDS: 'Сервер отклонил точку вне границ мира.',
}

export function SocialMapScreen({ world, initialTab = 'social', onClose, onOpenChat, onOpenParty }) {
  const [tab, setTab] = useState(initialTab === 'map' ? 'map' : 'social')
  const [state, setState] = useState(() => socialMapView(world))
  const [loaded, setLoaded] = useState(() => screenData(world, 'social-map') !== null)
  const [query, setQuery] = useState('')
  const handledResult = useRef(null)
  const status = worldStatus(world)

  useEffect(() => {
    const off = subscribeScreenData(world, 'social-map', () => {
      const next = socialMapView(world)
      setState(next)
      setLoaded(true)
      const result = next.lastResult
      if (result?.requestId && handledResult.current !== result.requestId) {
        handledResult.current = result.requestId
        world.ui.notify({
          kind: result.ok ? 'success' : 'error',
          title: result.ok ? 'ПЕРЕМЕЩЕНИЕ ПОДТВЕРЖДЕНО' : 'ПЕРЕМЕЩЕНИЕ ОТКЛОНЕНО',
          message: resultMessages[result.code] || 'Запрос обработан сервером.',
          dedupeKey: `social-map:${result.requestId}`,
        })
      }
    })
    refreshSocialMap(world)
    return off
  }, [world])

  const players = useMemo(() => buildPresenceList(state.social.players, { localId: state.social.selfUserId }), [state.social])
  const destinations = useMemo(() => searchMap(query, buildMapIndex({ destinations: state.map.destinations })), [query, state.map.destinations])
  const offline = status.connection === 'offline' || status.connection === 'error'

  return (
    <ScreenShell label='SOCIAL / MAP' title={tab === 'social' ? 'ЛЮДИ' : 'КАРТА'} subtitle='Только live-данные текущего мира' onClose={onClose}>
      <main css={css`flex:1;overflow:auto;padding:${space(3)};max-width:980px;width:100%;margin:0 auto;box-sizing:border-box;`}>
        <nav aria-label='Разделы' css={css`display:flex;gap:${space(1)};margin-bottom:${space(3)};`}>
          <Tab active={tab === 'social'} onClick={() => setTab('social')}><UsersIcon size={18}/>Люди</Tab>
          <Tab active={tab === 'map'} onClick={() => setTab('map')}><MapPinIcon size={18}/>Карта</Tab>
          <button type='button' aria-label='Обновить' onClick={() => refreshSocialMap(world)} css={buttonCss}><RefreshCwIcon size={18}/></button>
        </nav>

        {!loaded && <State title='ЗАГРУЗКА' text='Ждём проекцию присутствия и карты от сервера.' />}
        {loaded && (!state.available || offline) && <State title={offline ? 'НЕТ СВЯЗИ' : 'НЕДОСТУПНО'} text='Данные не берутся из кэша. Повтори после переподключения.' />}
        {loaded && state.available && !offline && tab === 'social' && (
          <section aria-label='Игроки' css={listCss}>
            {players.length === 0 && <State title='НИКОГО НЕТ' text='Сервер не вернул активных игроков.' />}
            {players.map(player => <article key={player.userId} css={rowCss}>
              <div><strong>{player.name}{player.isSelf ? ' (вы)' : ''}</strong><small>userId: {player.userId}</small><small>Присутствие: в текущем мире</small></div>
              <div css={css`display:flex;gap:${space(1)};flex-wrap:wrap;`}>
                {presenceActions(player, state.social.capabilities).map(action => <button key={action.id} type='button' css={buttonCss} onClick={() => action.id === 'direct' ? onOpenChat?.(action.userId) : onOpenParty?.(action.userId)}>{action.id === 'direct' && <MessageSquareTextIcon size={16}/>} {action.label}</button>)}
              </div>
            </article>)}
          </section>
        )}
        {loaded && state.available && !offline && tab === 'map' && <section>
          <input value={query} maxLength={80} onChange={event => setQuery(event.target.value)} placeholder='Поиск места или участка' aria-label='Поиск по карте' css={inputCss}/>
          {!state.map.capabilities.imagery && <p css={noteCss}>Картография и маршруты не поддерживаются. Ниже — только серверные направления.</p>}
          <div css={listCss}>{destinations.length === 0 ? <State title='НИЧЕГО НЕ НАЙДЕНО' text='Измени поиск или обнови список.' /> : destinations.map(item => <article key={item.id} css={rowCss}>
            <div><strong>{item.label}</strong><small>{item.kind === 'plot' ? 'Реальный участок' : 'Точка мира'}{item.ownerName ? ` · ${item.ownerName}` : ''}</small></div>
            {state.map.capabilities.teleport && item.available && <button type='button' css={buttonCss} onClick={() => { if (!teleportToDestination(world, item)) world.ui.notify({ kind:'error', title:'НЕТ СВЯЗИ', message:'Запрос не отправлен.', dedupeKey:'social-map:offline' }) }}>Перейти</button>}
          </article>)}</div>
        </section>}
      </main>
    </ScreenShell>
  )
}

function Tab({ active, children, onClick }) { return <button type='button' aria-pressed={active} onClick={onClick} css={css`${buttonCss};border-color:${active ? COLORS.cyan : COLORS.border};color:${active ? COLORS.cyan : COLORS.text};`}>{children}</button> }
function State({ title, text }) { return <div role='status' css={css`padding:${space(4)};text-align:center;border:1px dashed ${COLORS.border};border-radius:${RADII.lg};`}><strong>{title}</strong><p css={noteCss}>{text}</p></div> }
const buttonCss = css`display:inline-flex;align-items:center;justify-content:center;gap:${space(1)};min-height:44px;padding:0 ${space(2)};border:1px solid ${COLORS.border};border-radius:${RADII.md};background:${COLORS.bgPanel};color:${COLORS.text};cursor:pointer;transition:border-color ${MOTION.hover};&:focus-visible{outline:none;box-shadow:${SHADOWS.focus};}&:hover{border-color:${COLORS.cyan};}`
const listCss = css`display:grid;gap:${space(1)};`
const rowCss = css`display:flex;align-items:center;justify-content:space-between;gap:${space(2)};padding:${space(2)};border:1px solid ${COLORS.border};border-radius:${RADII.lg};background:${COLORS.bgPanel};strong,small{display:block;}small{margin-top:4px;color:${COLORS.textSecondary};font-size:${TYPE.caption.size};overflow-wrap:anywhere;}@media(max-width:640px){align-items:stretch;flex-direction:column;}`
const inputCss = css`width:100%;box-sizing:border-box;min-height:44px;padding:0 ${space(2)};border:1px solid ${COLORS.border};border-radius:${RADII.md};background:${COLORS.bgDeep};color:${COLORS.text};font-size:1rem;&:focus-visible{outline:none;box-shadow:${SHADOWS.focus};}`
const noteCss = css`color:${COLORS.textSecondary};font-size:${TYPE.caption.size};line-height:1.5;`
